import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Film, Crown, Zap, Sparkles, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import logo from "../assets/trimo-logo.png";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const { L } = useLang();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/clips/history").then(({ data }) => setHistory(data.jobs || [])).catch(() => {});
    api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const planLabel = { trial: L("Gratis", "Free"), pro: "Pro", premium: "Premium", vip: "VIP" }[user.plan || "trial"];
  const PlanIcon = { trial: Sparkles, pro: Zap, premium: Crown, vip: Crown }[user.plan || "trial"];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-16 border-b border-white/10 flex items-center justify-between px-5 sm:px-8">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="Vitled" className="h-8 w-8 rounded-lg" />
          <span className="text-lg font-bold">Vit<span className="text-yellow-400">led</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate("/studio")} className="bg-yellow-400 hover:bg-yellow-300 text-zinc-950 font-semibold rounded-xl">
            <Sparkles className="h-4 w-4 mr-1.5" /> Studio
          </Button>
          <Button onClick={logout} variant="ghost" className="text-zinc-300 hover:text-white rounded-xl">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="h-14 w-14 rounded-full" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-yellow-400/15 flex items-center justify-center text-yellow-400 font-bold text-xl">
              {(user.name || "U")[0]}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{user.name}</h1>
            <p className="text-sm text-zinc-400">{user.email}</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5">
            <div className="flex items-center gap-2 text-yellow-400"><PlanIcon className="h-5 w-5" /><span className="text-sm font-semibold">{L("Plan actual", "Current plan")}</span></div>
            <p className="mt-2 text-2xl font-extrabold">{planLabel}</p>
            {(user.plan || "trial") === "trial" && (
              <Link to="/#pricing" className="mt-2 inline-block text-sm text-yellow-400 hover:underline">{L("Mejorar plan", "Upgrade plan")}</Link>
            )}
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5" data-testid="dashboard-exports">
            <div className="flex items-center gap-2 text-yellow-400"><Film className="h-5 w-5" /><span className="text-sm font-semibold">{usage?.period === "lifetime" ? L("Exportaciones gratis", "Free exports") : L("Exportaciones este mes", "Exports this month")}</span></div>
            <p className="mt-2 text-2xl font-extrabold" data-testid="dashboard-exports-count">{usage ? `${usage.exports_used} / ${usage.exports_limit}` : "–"}</p>
            {usage && (
              <div className="mt-2 h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full" style={{ width: `${Math.min(100, (usage.exports_used / usage.exports_limit) * 100)}%` }} />
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-1">{usage ? L(`${usage.exports_remaining} exportaciones restantes`, `${usage.exports_remaining} exports left`) : ""}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5">
            <div className="flex items-center gap-2 text-yellow-400"><Clock className="h-5 w-5" /><span className="text-sm font-semibold">{L("Vídeos analizados", "Videos analyzed")}</span></div>
            <p className="mt-2 text-2xl font-extrabold">{history.length}</p>
          </div>
        </div>

        <h2 className="mt-10 text-xl font-bold">{L("Historial de clips", "Clip history")}</h2>
        <div className="mt-4 space-y-3">
          {history.length === 0 && (
            <p className="text-zinc-500 text-sm">{L("Aún no has analizado ningún vídeo.", "You haven't analyzed any video yet.")}</p>
          )}
          {history.map((job) => (
            <div key={job.job_id} className="flex items-center gap-4 rounded-xl border border-white/10 bg-zinc-900/50 p-3">
              <img src={job.thumb} alt="" className="h-14 w-24 object-cover rounded-lg" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{job.title}</p>
                <p className="text-xs text-zinc-500">{job.num_clips} clips · {new Date(job.created_at).toLocaleDateString()}</p>
              </div>
              <Button
                onClick={() => navigate("/studio", { state: { url: job.url } })}
                variant="outline"
                className="border-white/15 bg-transparent rounded-lg text-sm"
              >
                {L("Reabrir", "Reopen")}
              </Button>
            </div>
          ))}
        </div>

        <Link to="/studio" className="mt-8 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {L("Volver al Studio", "Back to Studio")}
        </Link>
      </div>
    </div>
  );
}
